import { v4 as uuidv4 } from 'uuid';

/**
 * Node graph of one page, shared by the canvas graph domains (Logic, Home
 * Assistant) — pure and immutable: every mutation returns a new graph, so
 * history can diff plain snapshots.
 *
 * Shape: { version, nodes: GraphNode[], wires: GraphWire[] }
 *   GraphNode: { id, kind, type, x, y, data }
 *   GraphWire: { id, from: Endpoint, to: Endpoint, order? }
 *   Endpoint:  { object: <uuid>, port } (a UI object) | { node: <id>, port }
 *
 * Either end of a wire can be a UI object, referenced by its Designer uuid.
 * Node coordinates are page-local. Which ports may connect, and what a graph
 * means, is up to each domain; nothing here knows about node kinds.
 */
export const GRAPH_VERSION = 1;

export function createGraph() {
    return { version: GRAPH_VERSION, nodes: [], wires: [] };
}

export function isEmptyGraph(graph) {
    return !graph || ((graph.nodes ?? []).length === 0 && (graph.wires ?? []).length === 0);
}

export function findNode(graph, id) {
    return (graph?.nodes ?? []).find(node => node.id === id) ?? null;
}

export function findWire(graph, id) {
    return (graph?.wires ?? []).find(wire => wire.id === id) ?? null;
}

export const isObjectEndpoint = endpoint => endpoint?.object !== undefined;

/** An endpoint reduced to its reference and port. */
export function endpointRef(endpoint) {
    return isObjectEndpoint(endpoint)
        ? { object: endpoint.object, port: endpoint.port }
        : { node: endpoint.node, port: endpoint.port };
}

export function addNode(graph, node) {
    return { ...graph, nodes: [...graph.nodes, node] };
}

function mapNode(graph, id, update) {
    return { ...graph, nodes: graph.nodes.map(node => node.id === id ? update(node) : node) };
}

export function moveNode(graph, id, x, y) {
    return mapNode(graph, id, node => ({ ...node, x, y }));
}

export function updateNodeData(graph, id, patch) {
    return mapNode(graph, id, node => ({ ...node, data: { ...node.data, ...patch } }));
}

/** Replace a node by one with the same id (e.g. a different type); its wires stay. */
export function replaceNode(graph, node) {
    return mapNode(graph, node.id, () => node);
}

/** Replace a node's data entirely (editors may remove keys). */
export function setNodeData(graph, id, data) {
    return mapNode(graph, id, node => ({ ...node, data: structuredClone(data) }));
}

const touchesNode = (wire, id) => wire.from.node === id || wire.to.node === id;

/** Remove a node and every wire attached to it. */
export function removeNode(graph, id) {
    return {
        ...graph,
        nodes: graph.nodes.filter(node => node.id !== id),
        wires: graph.wires.filter(wire => !touchesNode(wire, id)),
    };
}

export function removeWire(graph, id) {
    return { ...graph, wires: graph.wires.filter(wire => wire.id !== id) };
}

/** Remove every wire to or from a UI object (used when the object is deleted). Nodes stay. */
export function removeObjectRefs(graph, objectUuid) {
    return {
        ...graph,
        wires: graph.wires.filter(wire => wire.from.object !== objectUuid && wire.to.object !== objectUuid),
    };
}

/**
 * Append a wire between two endpoints, without any rule check — domains
 * check their rules first. @returns {{ graph, wire }}
 */
export function appendWire(graph, from, to, { id, order } = {}) {
    const wire = { id: id ?? `w-${uuidv4()}`, from: endpointRef(from), to: endpointRef(to) };
    if (order !== undefined) wire.order = order;
    return { graph: { ...graph, wires: [...graph.wires, wire] }, wire };
}

/** Rewrite the `order` of a node's outgoing wires; `wireIds` lists them in the new order. */
export function reorderNodeOutputs(graph, nodeId, wireIds) {
    const order = new Map(wireIds.map((wireId, index) => [wireId, index]));
    return {
        ...graph,
        wires: graph.wires.map(wire => wire.from.node === nodeId && order.has(wire.id)
            ? { ...wire, order: order.get(wire.id) }
            : wire),
    };
}

/**
 * Copy a subgraph with fresh node/wire ids. Object references are remapped
 * through `objectMap` (old uuid → new uuid); wires to unmapped objects or
 * nodes are dropped. Node positions shift by `offset`. New node ids are
 * `<idPrefix[kind]>-<uuid>`.
 */
export function cloneSubgraph(subgraph, { objectMap = new Map(), offset = { x: 0, y: 0 }, idPrefix = {} } = {}) {
    const nodeMap = new Map(subgraph.nodes.map(node => [node.id, `${idPrefix[node.kind] ?? 'nd'}-${uuidv4()}`]));
    const nodes = subgraph.nodes.map(node => ({
        ...structuredClone(node), id: nodeMap.get(node.id), x: node.x + offset.x, y: node.y + offset.y,
    }));
    const remap = endpoint => isObjectEndpoint(endpoint)
        ? (objectMap.has(endpoint.object) ? { object: objectMap.get(endpoint.object), port: endpoint.port } : null)
        : (nodeMap.has(endpoint.node) ? { node: nodeMap.get(endpoint.node), port: endpoint.port } : null);
    const wires = [];
    for (const wire of subgraph.wires) {
        const from = remap(wire.from);
        const to = remap(wire.to);
        if (!from || !to) continue;
        wires.push({ ...structuredClone(wire), id: `w-${uuidv4()}`, from, to });
    }
    return { nodes, wires };
}

/** Append a subgraph (as returned by cloneSubgraph) to a graph. */
export function mergeSubgraph(graph, subgraph) {
    const base = graph ?? createGraph();
    return { ...base, nodes: [...base.nodes, ...subgraph.nodes], wires: [...base.wires, ...subgraph.wires] };
}
